import React, { useEffect, useState } from "react";
import {
  Paper,
  Snackbar,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Tooltip,
  IconButton,
  Button,
  Chip,
  Alert,
  TablePagination,
  Checkbox,
  CircularProgress
} from "@mui/material";
import {
  AccessTime,
  CheckCircle,
  Search,
  Info,
  Edit,
  PendingActions,
  Timeline,
  Group
} from "@mui/icons-material";
import axios from "axios";
import { localAxiosGet } from "../../../Axios/axios";
import GoalDetails from "../../../components/GoalDetails";

/* ---------------- DASHBOARD CARDS (OLD STYLE KEPT) ---------------- */

const DASHBOARD_CARDS = [
  {
    key: "all",
    title: "Total Goals",
    icon: Group,
    color: "#2196F3"
  },
  {
    key: "Pending",
    title: "Pending Goals",
    icon: PendingActions,
    color: "#FB8C00"
  },
  {
    key: "Accepted",
    title: "Accepted Goals",
    icon: CheckCircle,
    color: "#4CAF50"
  },
  {
    key: "Completed",
    title: "Completed Goals",
    icon: Timeline,
    color: "#7E57C2"
  }
];


/* ---------------- COMPONENT ---------------- */

const HREmployeeGoalsReview = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [cardFilter, setCardFilter] = useState("all");

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [openGoalDetails, setOpenGoalDetails] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  /* ---------------- FETCH GOALS ---------------- */

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const status =
        cardFilter !== "all"
          ? cardFilter
          : statusFilter !== "All"
            ? statusFilter
            : "";

      const res = await axios(
        localAxiosGet(
          `/performanceHr/get_all_goals?status=${status}&page=${page + 1}&limit=${rowsPerPage}`
        )
      );

      if (res.data.status === "SUCCESS") {
        setRows(res.data.result.rows || []);
        setTotalCount(res.data.result.rowsCount || 0);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGoals();
  }, [page, rowsPerPage, statusFilter, cardFilter]);

  /* ---------------- SEARCH FILTER ---------------- */

  const filteredRows = rows.filter((row) => {
    const name = `${row.employee.first_name} ${row.employee.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  /* ---------------- STATUS CHIP ---------------- */

  const getStatusChip = (status) => {
    switch (status) {
      case "Pending":
        return <Chip label="Pending" color="warning" size="small" icon={<AccessTime />} />;
      case "Accepted":
        return <Chip label="Accepted" color="info" size="small" />;
      case "Completed":
        return <Chip label="Completed" color="success" size="small" icon={<CheckCircle />} />;
      case "Rejected":
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  /* ---------------- SELECTION ---------------- */

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredRows.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ---------------- RENDER ---------------- */

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Goals Review
      </Typography>

      {/* -------- DASHBOARD CARDS (UNCHANGED STYLE) -------- */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {DASHBOARD_CARDS.map((card) => {
          const Icon = card.icon;
          const isActive = cardFilter === card.key;

          return (
            <Box
              key={card.key}
              onClick={() => {
                setCardFilter(card.key);
                setPage(0);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 20,
                cursor: "pointer",
                border: `1px solid ${card.color}`,
                color: isActive ? "#fff" : card.color,
                backgroundColor: isActive ? card.color : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: isActive
                    ? card.color
                    : `${card.color}20`
                }
              }}
            >
              <Icon fontSize="small" />
              <Typography fontSize={14} fontWeight={500}>
                {card.title}
              </Typography>
            </Box>
          );
        })}
      </Box>


      {/* -------- FILTERS -------- */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search employee..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1 }} /> }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Accepted">Accepted</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* -------- TABLE -------- */}
      {loading ? (
        <Box py={4} textAlign="center">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead sx={{ background: "#F1F5F9" }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      selectedIds.length === filteredRows.length &&
                      filteredRows.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Department ID</TableCell>
                <TableCell>Designation ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onChange={() => handleSelectRow(row.id)}
                    />
                  </TableCell>

                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar>{row.employee.first_name[0]}</Avatar>
                      {row.employee.first_name} {row.employee.last_name}
                    </Box>
                  </TableCell>

                  {/* 🔑 IDs ONLY (NO DUMMY DATA) */}
                  <TableCell>{row.employee.department}</TableCell>
                  <TableCell>{row.employee.designation}</TableCell>

                  <TableCell>{getStatusChip(row.status)}</TableCell>

                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedGoal(row.id);
                          setOpenGoalDetails(true);
                        }}
                      >
                        <Info />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Review">
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {!filteredRows.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* -------- PAGINATION -------- */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(+e.target.value);
          setPage(0);
        }}
      />

      {/* -------- GOAL DETAILS -------- */}
      <GoalDetails
        isOpen={openGoalDetails}
        onClose={() => setOpenGoalDetails(false)}
        selectedGoal={selectedGoal}
        isManager
      />

      {/* -------- SNACKBAR -------- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default HREmployeeGoalsReview;
